import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSetPageTitle } from '../../hooks/useSetPageTitle';
import { AppLayout } from '../../components/layout/app-layout';
import {
  OPERATIONS_DEFAULT_TAB,
  OPERATIONS_SIDEBAR,
  OPERATIONS_TABS,
} from '../../components/layout/operations-nav.config';
import { useGetDataJobFailuresQuery, useGetSchedulerFailuresQuery } from '../../redux/operations/operations.endpoints';
import { getIsUserStaff } from '../../redux/user/user.selectors';
import EtlDashboardTab from './tabs/etl-dashboard.tab';
import SchedulerDashboardTab from './tabs/scheduler-dashboard.tab';
import LogMonitoringTab from './tabs/log-monitoring.tab';
import CreativeTemplatesDashboardTab from './tabs/creative-templates-dashboard.tab';
import DataTransfersTab from './tabs/data-transfers.tab';
import UsageInsightsTab from './tabs/usage-insights.tab';

const OperationsPage = () => {
  useSetPageTitle('Operations');
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const isStaff = useSelector(getIsUserStaff);

  const { data: schedulerFailures } = useGetSchedulerFailuresQuery(undefined, {
    pollingInterval: 120_000,
  });
  const { data: dataJobFailures } = useGetDataJobFailuresQuery(undefined, {
    pollingInterval: 120_000,
  });
  const schedulerFailureCount = schedulerFailures?.failure_count ?? 0;
  const hasDataJobFailures = Boolean(dataJobFailures?.has_alerts);

  const visibleTabs = useMemo(
    () => OPERATIONS_TABS.filter((operationsTab) => (
      operationsTab.id !== 'usage-insights' || isStaff
    )),
    [isStaff],
  );

  const visibleTabIds = useMemo(
    () => visibleTabs.map((operationsTab) => operationsTab.id),
    [visibleTabs],
  );

  const operationsTabs = useMemo(
    () =>
      visibleTabs.map((operationsTab) => {
        if (operationsTab.id === 'scheduler' && schedulerFailureCount > 0) {
          return { ...operationsTab, badge: schedulerFailureCount };
        }
        if (operationsTab.id === 'etl' && hasDataJobFailures) {
          return { ...operationsTab, badge: 1, badgeVariant: 'dot' as const };
        }
        return operationsTab;
      }),
    [hasDataJobFailures, schedulerFailureCount, visibleTabs],
  );

  const currentTab = visibleTabIds.includes(tab ?? '')
    ? (tab as string)
    : OPERATIONS_DEFAULT_TAB;

  useEffect(() => {
    if (tab === 'data-jobs') {
      const search = window.location.search;
      navigate(`/operations/etl${search}`, { replace: true });
      return;
    }
    if (tab === 'templates' || tab === 'vertical-profiles') {
      navigate('/operations/creative-templates', { replace: true });
      return;
    }
    if (!tab || !visibleTabIds.includes(tab)) {
      navigate(`/operations/${OPERATIONS_DEFAULT_TAB}`, { replace: true });
    }
  }, [navigate, tab, visibleTabIds]);

  const handleTabChange = (tabId: string) => {
    navigate(`/operations/${tabId}`);
  };

  return (
    <AppLayout
      activePage={OPERATIONS_SIDEBAR.path}
      navbarProps={{
        tabs: operationsTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      <div className="h-[calc(100vh-8rem)]">
        {currentTab === 'etl' && <EtlDashboardTab />}
        {currentTab === 'scheduler' && <SchedulerDashboardTab />}
        {currentTab === 'log-monitoring' && <LogMonitoringTab />}
        {currentTab === 'creative-templates' && <CreativeTemplatesDashboardTab />}
        {currentTab === 'data-transfers' && <DataTransfersTab />}
        {currentTab === 'usage-insights' && isStaff && <UsageInsightsTab />}
      </div>
    </AppLayout>
  );
};

export default OperationsPage;
